<!--
  shopping-cart-modal.vue
  
  2025-06-04: Created shopping cart modal component extracted from site header
-->
<template>
  <v-dialog
    :value="show"
    @input="$emit('update:show', $event)"
    max-width="600px"
    persistent
  >
    <v-card class="shopping-cart-modal">
      <v-card-title class="d-flex align-center justify-space-between">
        <span class="text-h6 font-weight-semibold">{{
          $t("shopping_cart")
        }}</span>
        <v-btn icon @click="$emit('update:show', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pt-4">
        <div v-if="cartItems.length === 0" class="text-center py-8">
          <v-icon size="64" color="grey lighten-2">mdi-cart-outline</v-icon>
          <div class="text-h6 grey--text mt-4">
            {{ $t("your_cart_is_empty") }}
          </div>
        </div>

        <div v-else>
          <v-list class="pa-0">
            <v-list-item
              v-for="item in cartItems"
              :key="item.id"
              class="cart-item px-0"
            >
              <v-list-item-avatar size="80" class="mr-4">
                <v-img
                  :src="item.image"
                  :alt="item.listing_title"
                  cover
                  class="rounded"
                />
              </v-list-item-avatar>

              <v-list-item-content>
                <v-list-item-title class="font-weight-semibold mb-1">
                  {{ item.listing_title }}
                </v-list-item-title>
                <v-list-item-subtitle class="text-body-2">
                  {{ formatDate(item.check_in) }} -
                  {{ formatDate(item.check_out) }}
                  <span class="mx-1">•</span>
                  {{ item.adults }} {{ $t("adults") }}
                  <span v-if="item.children > 0">
                    • {{ item.children }} {{ $t("children") }}
                  </span>
                </v-list-item-subtitle>
                <div v-if="item.total_price" class="font-weight-medium mt-2">
                  {{
                    toDisplayCurrency(item.total_price, settings, item.currency)
                  }}
                </div>
              </v-list-item-content>

              <v-list-item-action>
                <v-btn
                  icon
                  small
                  color="error"
                  @click="removeFromCart(item.id)"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>

          <v-divider class="my-4" />

          <!-- Email Input -->
          <v-text-field
            v-model="quoteEmail"
            :label="$t('email_for_quote')"
            type="email"
            outlined
            dense
            :rules="emailRules"
            required
            class="mb-4"
          />
        </div>
      </v-card-text>

      <v-card-actions v-if="cartItems.length > 0" class="px-6 pb-4">
        <v-btn text @click="clearCart" color="error">
          {{ $t("clear_all") }}
        </v-btn>
        <v-spacer />
        <v-btn
          :color="primaryColor"
          class="white--text"
          elevation="0"
          :loading="sendingQuote"
          :disabled="!quoteEmail || !isValidEmail(quoteEmail)"
          @click="sendQuote"
        >
          {{ $t("send_quote") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import StylesMixin from "@/mixins/styles-mixin";
import FormattersMixin from "@/mixins/formatters-mixin";
import { axios } from "@/store/fetch";
import { mapState } from "vuex";

export default {
  name: "ShoppingCartModal",
  mixins: [StylesMixin, FormattersMixin],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      cartItems: [],
      quoteEmail: "",
      sendingQuote: false,
    };
  },
  computed: {
    ...mapState({
      settings: (state) => state.settingsModule.settings,
    }),
    emailRules() {
      return [
        (v) => !!v || "Email is required",
        (v) => /.+@.+\..+/.test(v) || "Email must be valid",
      ];
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.loadCartItems();
      }
    },
  },
  mounted() {
    this.loadCartItems();
    window.addEventListener("storage", this.handleStorageChange);
  },
  beforeDestroy() {
    window.removeEventListener("storage", this.handleStorageChange);
  },
  methods: {
    loadCartItems() {
      const cartData = localStorage.getItem("shopping_cart");
      this.cartItems = cartData ? JSON.parse(cartData) : [];
      this.$emit("cart-count-changed", this.cartItems.length);
    },

    handleStorageChange(event) {
      if (event.key === "shopping_cart") {
        this.loadCartItems();
      }
    },

    removeFromCart(itemId) {
      this.cartItems = this.cartItems.filter((item) => item.id !== itemId);
      localStorage.setItem("shopping_cart", JSON.stringify(this.cartItems));
      this.$emit("cart-count-changed", this.cartItems.length);

      // Dispatch custom event for immediate cart count update
      window.dispatchEvent(
        new CustomEvent("cartUpdated", {
          detail: { cartItems: this.cartItems, action: "removed", itemId },
        })
      );
    },

    clearCart() {
      this.cartItems = [];
      localStorage.removeItem("shopping_cart");
      this.$emit("cart-count-changed", 0);

      // Dispatch custom event for immediate cart count update
      window.dispatchEvent(
        new CustomEvent("cartUpdated", {
          detail: { cartItems: [], action: "cleared" },
        })
      );
    },

    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },

    formatDate(dateString) {
      if (!dateString) return "";
      return this.$moment(dateString).format("MMM DD, YYYY");
    },

    formatCurrency(amount, currency) {
      if (!amount) return "";
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency || "USD",
      }).format(amount);
    },

    async sendQuote() {
      if (!this.isValidEmail(this.quoteEmail) || this.cartItems.length === 0) {
        return;
      }

      this.sendingQuote = true;

      try {
        const payload = {
          email: this.quoteEmail,
          cart_items: this.cartItems,
          requested_at: new Date().toISOString(),
        };

        const response = await axios.post("/create-quote", payload);

        if (response.status === 200) {
          // Show success message
          this.$nextTick(() => {
            alert("Quote request sent successfully!");
          });

          // Clear cart and close modal
          this.clearCart();
          this.quoteEmail = "";
          this.$emit("update:show", false);
          this.$emit("quote-sent");
        }
      } catch (error) {
        console.error("Failed to send quote:", error);
        alert("Failed to send quote. Please try again.");
      } finally {
        this.sendingQuote = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.shopping-cart-modal {
  .cart-item {
    border-bottom: 1px solid #e0e0e0;

    &:last-child {
      border-bottom: none;
    }
  }

  .v-list-item__avatar {
    margin-top: 8px;
    margin-bottom: 8px;
  }
}
</style>
